import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useCreateMeter, useUpdateMeter, useKPIs } from '../hooks/useMeters';
import '../styles/MeterForm.css';

/** Marks a field as mandatory, replacing the bare asterisk in the label text. */
const Required = () => <span className="im-mform-req">*</span>;

/**
 * Helper text under a field.
 *
 * Deliberately not `.text-muted`: a global rule in `sidebar/common.css` gives
 * that class display:flex, text-align:center and text-transform:uppercase, so
 * every hint using it renders as centred capitals on its own line.
 *
 * @param {boolean} locked - Shows a padlock, for fields fixed after creation.
 * @param {React.ReactNode} children - The helper sentence.
 */
const Hint = ({ locked = false, children }) => (
  <Form.Text className={`im-mform-hint${locked ? ' im-mform-hint--lock' : ''}`}>
    {children}
  </Form.Text>
);

/**
 * Heading for one group of fields.
 * @param {string} sub - Optional qualifier shown beside the heading.
 * @param {React.ReactNode} children - The heading text.
 */
const Section = ({ sub, children }) => (
  <p className="im-mform-section">
    {children}
    {sub && <span className="im-mform-section-sub">{sub}</span>}
  </p>
);

const MeterForm = forwardRef(({ onSave, sources, subLocations }, ref) => {
  const [show, setShow] = useState(false);
  const [currentMeter, setCurrentMeter] = useState(null);
  const [formData, setFormData] = useState({
    meterId: '',
    meterName: '',
    meterType: 'WATER_METER',
    gatewayId: '',
    sourceId: 0,
    subLocationId: 0,
    unit: '',
    measurementType: 'FLOW',
    inactiveDurationMinutes: 15,
    kpiId: 0 // For current financial year
  });
  const [errors, setErrors] = useState({});

  const createMutation = useCreateMeter();
  const updateMutation = useUpdateMeter(currentMeter?.meterId || '');
  
  // Fetch EXTERNAL KPIs only (users can only configure EXTERNAL KPIs)
  const { data: kpis = [], isLoading: kpisLoading } = useKPIs({ kpiType: 'EXTERNAL' });

  useImperativeHandle(ref, () => ({
    show: (meter) => {
      if (meter) {
        setCurrentMeter(meter);
        // Get current FY KPI
        const currentFYKpi = meter.kpiMappings?.find(
          mapping => mapping.financialYear === '2025-2026'
        );
        
        setFormData({
          meterId: meter.meterId,
          meterName: meter.meterName,
          meterType: meter.meterType,
          gatewayId: meter.gatewayId,
          sourceId: meter.sourceId,
          subLocationId: meter.subLocationId,
          unit: meter.unit,
          measurementType: meter.measurementType,
          inactiveDurationMinutes: meter.inactiveDurationMinutes ?? 15,
          kpiId: currentFYKpi?.kpiId || 0
        });
      } else {
        setCurrentMeter(null);
        setFormData({
          meterId: '',
          meterName: '',
          meterType: 'WATER_METER',
          gatewayId: '',
          sourceId: 0,
          subLocationId: 0,
          unit: '',
          measurementType: 'FLOW',
          inactiveDurationMinutes: 15,
          kpiId: 0
        });
      }
      setErrors({});
      setShow(true);
    },
    hide: () => {
      setShow(false);
      setCurrentMeter(null);
      setErrors({});
    }
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.meterId) newErrors.meterId = 'Meter ID is required';
    if (!formData.meterName) newErrors.meterName = 'Meter Name is required';
    if (!formData.gatewayId) newErrors.gatewayId = 'Gateway ID is required';
    if (!formData.sourceId) newErrors.sourceId = 'Location is required';
    if (!formData.subLocationId) newErrors.subLocationId = 'Sub Location is required';
    if (!formData.unit) newErrors.unit = 'Unit is required';
    if (formData.inactiveDurationMinutes === undefined || formData.inactiveDurationMinutes < 1) {
      newErrors.inactiveDurationMinutes = 'Inactive duration must be at least 1 minute';
    }

    // KPI is NOT required during creation, only when editing

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const submitData = {
        meterId: formData.meterId,
        meterName: formData.meterName,
        meterType: formData.meterType,
        gatewayId: formData.gatewayId,
        sourceId: formData.sourceId,
        subLocationId: formData.subLocationId,
        unit: formData.unit,
        measurementType: formData.measurementType,
        inactiveDurationMinutes: formData.inactiveDurationMinutes ?? 15
      };

      // Only include KPI mapping if KPI is selected
      if (formData.kpiId) {
        submitData.kpiId = formData.kpiId;
        submitData.financialYear = '2025-2026'; // Current FY
      }

      if (currentMeter) {
        await updateMutation.mutateAsync(submitData);
      } else {
        await createMutation.mutateAsync(submitData);
      }
      setShow(false);
      onSave();
    } catch (error) {
      console.error('Error saving meter:', error);
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const error = createMutation.error || updateMutation.error;

  // Group KPIs by hierarchy
  const groupedKPIs = kpis.reduce((acc, kpi) => {
    const key = `${kpi.module} > ${kpi.category}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(kpi);
    return acc;
  }, {});

  return (
    <Modal
      className="im-modal im-mform"
      show={show}
      onHide={() => setShow(false)}
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{currentMeter ? 'Edit Meter' : 'Add New Meter'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error instanceof Error ? error.message : 'An error occurred'}
            </Alert>
          )}

          <Section>Basic Information</Section>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meter ID <Required /></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.meterId}
                  onChange={(e) => handleChange('meterId', e.target.value)}
                  isInvalid={!!errors.meterId}
                  disabled={!!currentMeter}
                  placeholder="e.g., 3K822500393969"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.meterId}
                </Form.Control.Feedback>
                {currentMeter && <Hint locked>Meter ID cannot be changed</Hint>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meter Name <Required /></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.meterName}
                  onChange={(e) => handleChange('meterName', e.target.value)}
                  isInvalid={!!errors.meterName}
                  placeholder="e.g., Main Building Water Meter"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.meterName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Gateway ID <Required /></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.gatewayId}
                  onChange={(e) => handleChange('gatewayId', e.target.value)}
                  isInvalid={!!errors.gatewayId}
                  disabled={!!currentMeter}
                  placeholder="e.g., GTW-001"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.gatewayId}
                </Form.Control.Feedback>
                {currentMeter && <Hint locked>Gateway ID cannot be changed</Hint>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Unit <Required /></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  isInvalid={!!errors.unit}
                  disabled={!!currentMeter}
                  placeholder="e.g., kL, kWh, m³"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.unit}
                </Form.Control.Feedback>
                {currentMeter && <Hint locked>Unit cannot be changed</Hint>}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meter Type <Required /></Form.Label>
                <Form.Select
                  value={formData.meterType}
                  onChange={(e) => handleChange('meterType', e.target.value)}
                >
                  <option value="WATER_METER">Water Meter</option>
                  <option value="ENERGY_METER">Energy Meter</option>
                  <option value="GAS_METER">Gas Meter</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Measurement Type <Required /></Form.Label>
                <Form.Select
                  value={formData.measurementType}
                  onChange={(e) => handleChange('measurementType', e.target.value)}
                >
                  <option value="CUMULATIVE">CUMULATIVE</option>
                  <option value="INTERVAL">INTERVAL</option>
                  <option value="INSTANT">INSTANT</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Section>Location Information</Section>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Location <Required /></Form.Label>
                <Form.Select
                  value={formData.sourceId}
                  onChange={(e) => handleChange('sourceId', parseInt(e.target.value))}
                  isInvalid={!!errors.sourceId}
                >
                  <option value="">Select Location</option>
                  {sources && sources.map(source => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.sourceId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sub Location <Required /></Form.Label>
                <Form.Select
                  value={formData.subLocationId}
                  onChange={(e) => handleChange('subLocationId', parseInt(e.target.value))}
                  isInvalid={!!errors.subLocationId}
                >
                  <option value="">Select Sub Location</option>
                  {subLocations && subLocations.map(subLocation => (
                    <option key={subLocation.value} value={subLocation.value}>
                      {subLocation.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.subLocationId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Section sub={!currentMeter ? '(Optional - can be configured later)' : null}>
            KPI Configuration (Current Financial Year)
          </Section>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Select KPI (EXTERNAL){' '}
                  {!currentMeter && <span className="im-mform-optional">(Optional)</span>}
                </Form.Label>
                {kpisLoading ? (
                  <div className="im-mform-loading">
                    <Spinner animation="border" size="sm" />
                    <span>Loading KPIs...</span>
                  </div>
                ) : (
                  <Form.Select
                    value={formData.kpiId}
                    onChange={(e) => handleChange('kpiId', parseInt(e.target.value))}
                    isInvalid={!!errors.kpiId}
                  >
                    <option value="">Select KPI (Optional)</option>
                    {Object.entries(groupedKPIs).map(([group, kpiList]) => (
                      <optgroup key={group} label={group}>
                        {kpiList.map(kpi => (
                          <option key={kpi.id} value={kpi.id}>
                            {kpi.subCategory ? `${kpi.subCategory} > ` : ''}{kpi.kpi}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Form.Select>
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.kpiId}
                </Form.Control.Feedback>
                <Hint>
                  {currentMeter
                    ? 'Only EXTERNAL KPIs can be configured. INTERNAL KPIs (Volume Flow, Mass Totalizer) are system-managed.'
                    : 'KPI configuration is optional during creation. You can configure it later by editing the meter. Only EXTERNAL KPIs can be configured.'}
                </Hint>
              </Form.Group>
            </Col>
          </Row>

          <Section>Inactivity Settings</Section>

          <Form.Group className="mb-3">
            <Form.Label>Inactive Duration (minutes) <Required /></Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={formData.inactiveDurationMinutes}
              onChange={(e) =>
                handleChange(
                  'inactiveDurationMinutes',
                  parseInt(e.target.value, 10) || 1
                )
              }
              isInvalid={!!errors.inactiveDurationMinutes}
            />
            <Form.Control.Feedback type="invalid">
              {errors.inactiveDurationMinutes}
            </Form.Control.Feedback>
            <Hint>
              The meter will be marked inactive if no data is received for this duration.
            </Hint>
          </Form.Group>

        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              currentMeter ? 'Update Meter' : 'Create Meter'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
});

MeterForm.displayName = 'MeterForm';

export default MeterForm;